import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { AboutHeadline, PillarsGrid, StatsBanner, AboutCTA } from "@/components/AboutStory";
import { BRAND } from "@/assets";
import { ArrowRight, ShieldCheck } from "lucide-react";

export default function AboutPage() {
  useEffect(() => {
    document.title = "About Us | DGE: Cold-Blooded Records LLC™";
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, []);

  return (
    <main className="bg-obsidian text-ice min-h-screen" data-testid="about-page">
      <Navbar />
      <div className="pt-[70px]">
        <section className="relative py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
            <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
              <div>
                <AboutHeadline />
                <Reveal delay={0.15}>
                  <div className="mt-8 space-y-5 text-sm sm:text-base text-ice/75 leading-relaxed" data-testid="about-narrative">
                    <p>
                      DGE: Cold-Blooded Records LLC™ (Dirt Gang Entertainment) is an independent music
                      powerhouse and lifestyle enterprise built on the radical premise that real artists
                      should own their craft, control their catalogs, and dictate their legacy.
                    </p>
                    <p>
                      Founded through a shared vision between lead artist and executive producer{" "}
                      <strong className="text-white">Heather Da Boa</strong> and business manager and
                      mentor <strong className="text-white">Sherrod &quot;HotRod&quot; Whiteside</strong>,
                      DGE emerged from a pivotal alliance in the studio. What began as HotRod discovering
                      and molding Heather Da Boa&apos;s raw talent into a charting independent force evolved
                      into a powerful co-founding partnership. Together, they forged a full-scale record
                      label, artist development engine, and lifestyle brand designed for long-term equity
                      and independent dominance.
                    </p>
                    <p>
                      We are the originators of{" "}
                      <strong className="text-venom">Cold-Blooded Soundscapes™</strong> — a trademarked
                      sonic lane where heavy 808 sub-bass, tight drums, raw storytelling, and atmospheric
                      R&amp;B/trap textures intersect.
                    </p>
                  </div>
                </Reveal>
                <Reveal delay={0.25}>
                  <Link
                    to="/team"
                    className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-venom hover:text-white transition-colors duration-300"
                    data-testid="about-team-link"
                  >
                    Meet the Executive Leadership <ArrowRight size={16} />
                  </Link>
                </Reveal>
              </div>

              <Reveal delay={0.1} className="relative lg:sticky lg:top-28">
                <div className="relative">
                  <div className="absolute -inset-4 bg-crystal/20 blur-[60px] rounded-[2rem]" aria-hidden="true" />
                  <div className="relative rounded-2xl overflow-hidden border border-crystal/35 shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
                    <img
                      src={BRAND.founders}
                      alt="Heather Da Boa and Sherrod 'HotRod' Whiteside in the DGE studio"
                      className="w-full aspect-[3/4] sm:aspect-[4/5] object-cover"
                      loading="lazy"
                      data-testid="about-founders-photo"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 glass-panel rounded-xl px-4 py-3">
                      <ShieldCheck size={20} className="text-venom shrink-0" />
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ice/90">
                        Official ASCAP® &amp; PRO Members · Est. 2026
                      </span>
                    </div>
                  </div>
                  <img
                    src="/logo-coin.png"
                    alt=""
                    aria-hidden="true"
                    className="absolute -top-6 right-2 w-24 sm:w-28 drop-shadow-[0_0_30px_rgba(138,43,226,0.5)] rotate-[8deg]"
                  />
                </div>
              </Reveal>
            </div>

            <div className="mt-16 lg:mt-24 space-y-16 lg:space-y-20">
              <div>
                <Reveal className="mb-8">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium">
                    Core Pillars
                  </p>
                </Reveal>
                <PillarsGrid />
              </div>
              <StatsBanner />
              <AboutCTA />
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}
